// Agents Module — page UI (modals, table, window.* for onclick) loads with this module graph
import '../pages/agents-page.js';
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';
import { hasPermission } from '../utils/permissions.js';

export async function loadAgents() {
    if (!hasPermission('view:agents')) {
        console.log('[Agents] No permission to view agents');
        return;
    }

    try {
        const agents = await api.getAgents();
        console.log('[Agents] Loaded:', agents);
        const list = Array.isArray(agents) ? agents : [];
        // Satu sumber render baris: agents-page.js (filter tab + modal pakai allAgents yang sama)
        if (typeof window.syncAgentsPageCache === 'function') {
            window.syncAgentsPageCache(list);
        } else {
            updateAgentsUI(list);
        }
    } catch (error) {
        console.error('[Agents] Failed to load:', error);
        showNotification('Failed to load agents', 'error');
    }
}

function updateAgentsUI(agents) {
    const list = document.getElementById('fleet-list');
    if (!list) {
        console.warn('[Agents] fleet-list element not found');
        return;
    }

    if (!agents || agents.length === 0) {
        list.innerHTML = `
            <tr>
                <td colspan="8" class="px-6 py-8 text-center text-slate-500 italic">
                    No managed servers found. Click "Add New Server" to get started.
                </td>
            </tr>
        `;
        if (typeof window.updateFleetSortHeaderIcons === 'function') {
            window.updateFleetSortHeaderIcons();
        }
        return;
    }

    const rows =
        typeof window.__applyFleetTableSort === 'function'
            ? window.__applyFleetTableSort(agents)
            : agents;

    // Must match <thead> in agents.html (8 columns): Host/IP, License, Type, Customer, Package, Expires, Status, Actions
    list.innerHTML = rows.map(agent => {
        const statusColor = agent.status === 'online' ? 'text-green-400' :
            agent.status === 'warning' ? 'text-yellow-400' : 'text-red-400';
        const statusBg = agent.status === 'online' ? 'bg-green-400' :
            agent.status === 'warning' ? 'bg-yellow-400' : 'bg-red-400';
        const typeLabel = (agent.server_type || 'vps').toUpperCase();
        const typeClass = typeLabel === 'DEDICATED' ? 'bg-purple-900 text-purple-300' : 'bg-blue-900 text-blue-300';

        const pkgLabel = (agent.package || 'trial').toUpperCase();
        const pkgClass = pkgLabel === 'PRO' ? 'bg-neonCyan/20 text-neonCyan' :
            pkgLabel === 'FREE' ? 'bg-green-900/50 text-green-400' : 'bg-amber-900/50 text-amber-400';

        const expiry = agent.license_expiry ? new Date(agent.license_expiry) : null;
        const expiryStr = expiry ? expiry.toLocaleDateString() : '--';
        const isExpired = expiry && expiry < new Date();
        const expiryClass = isExpired ? 'text-red-400' : 'text-slate-400';

        const licenseKey = agent.license_key || 'N/A';
        const idJs = JSON.stringify(agent.id);
        const lkAttr = encodeURIComponent(String(licenseKey));
        const canManage = hasPermission('manage:agents');
        const editBtn = canManage
            ? `<button type="button" onclick='event.stopPropagation(); openEditServerModal(${idJs})'
                            class="text-blue-400 hover:text-white px-2 py-1 text-xs border border-blue-900 rounded hover:bg-blue-900/30" title="Edit Server">
                            <i class="fas fa-edit"></i>
                        </button>`
            : '';
        const deleteBtn = canManage
            ? `<button type="button" onclick='event.stopPropagation(); deleteManagedAgent(${idJs})'
                            class="text-red-400 hover:text-white px-2 py-1 text-xs border border-red-900 rounded hover:bg-red-900/30" title="Delete Server">
                            <i class="fas fa-trash"></i>
                        </button>`
            : '';

        return `
            <tr class="hover:bg-slate-800/30 transition-all">
                <td class="px-4 py-4">
                    <div class="font-bold text-white cursor-pointer hover:text-neonCyan" onclick='viewAgent(${idJs})'>${escapeHtml(agent.hostname || 'Unknown')}</div>
                    <div class="text-xs text-slate-500 font-mono">${escapeHtml(agent.ip_address || '--')}</div>
                </td>
                <td class="px-4 py-4">
                    <div class="flex items-center gap-2">
                        <code class="text-xs text-neonCyan bg-slate-800 px-2 py-1 rounded font-mono">${escapeHtml(licenseKey)}</code>
                        <button type="button" data-lk="${lkAttr}" onclick="event.stopPropagation(); copyLicenseKey(decodeURIComponent(this.dataset.lk))" class="text-slate-500 hover:text-white text-xs" title="Copy">
                            <i class="fas fa-copy"></i>
                        </button>
                    </div>
                </td>
                <td class="px-4 py-4">
                    <span class="px-2 py-1 rounded text-[10px] font-bold ${typeClass} border border-white/10">${typeLabel}</span>
                </td>
                <td class="px-4 py-4 text-slate-400 text-xs">${escapeHtml(agent.client_name || 'Unassigned')}</td>
                <td class="px-4 py-4">
                    <span class="px-2 py-1 rounded text-[10px] font-bold ${pkgClass}">${pkgLabel}</span>
                </td>
                <td class="px-4 py-4 text-xs ${expiryClass}">${isExpired ? '⚠ ' : ''}${escapeHtml(expiryStr)}</td>
                <td class="px-4 py-4">
                    <div class="flex items-center gap-2">
                        <span class="w-2 h-2 rounded-full ${statusBg}"></span>
                        <span class="${statusColor} text-xs uppercase font-bold">${escapeHtml(agent.status || '')}</span>
                    </div>
                </td>
                <td class="px-4 py-4 text-center">
                    <div class="flex justify-center gap-1">
                        <button type="button" onclick='showInstallCommand(${idJs})'
                            class="text-green-400 hover:text-white px-2 py-1 text-xs border border-green-900 rounded hover:bg-green-900/30" title="Install Command">
                            <i class="fas fa-terminal"></i>
                        </button>
                        <button type="button" onclick='triggerScan(${idJs})'
                            class="text-neonCyan hover:text-white px-2 py-1 text-xs border border-neonCyan/30 rounded hover:bg-neonCyan/10" title="Scan">
                            <i class="fas fa-search"></i>
                        </button>
                        <button type="button" onclick='openRenewModal(${idJs})'
                            class="text-amber-400 hover:text-white px-2 py-1 text-xs border border-amber-900 rounded hover:bg-amber-900/30" title="Renew">
                            <i class="fas fa-sync"></i>
                        </button>
                        <button type="button" onclick='viewAgent(${idJs})'
                            class="text-slate-400 hover:text-white px-2 py-1 text-xs border border-slate-700 rounded hover:bg-slate-700" title="View Details">
                            <i class="fas fa-eye"></i>
                        </button>
                        ${editBtn}
                        ${deleteBtn}
                    </div>
                </td>
            </tr>
        `;
    }).join('');
    if (typeof window.updateFleetSortHeaderIcons === 'function') {
        window.updateFleetSortHeaderIcons();
    }
}

function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function viewAgent(id) {
    window.location.href = '/agents/' + id;
}

function scheduleAgentScanningTabRefresh() {
    if (typeof window.__reloadAgentScanningTab === 'function') {
        window.__reloadAgentScanningTab();
        [3000, 8000, 15000, 30000, 60000, 120000].forEach((ms) =>
            setTimeout(() => window.__reloadAgentScanningTab(), ms)
        );
    }
}

export async function triggerScan(agentId) {
    if (!confirm("Are you sure you want to trigger a remote security scan?")) return;

    try {
        await api.triggerScan(agentId);
        showNotification('Scan command initiated successfully!', 'success');
        scheduleAgentScanningTabRefresh();
    } catch (error) {
        console.error('[Agents] Scan failed:', error);
        showNotification('Failed to initiate scan', 'error');
    }
}

export async function deleteManagedAgent(agentId) {
    if (!hasPermission('manage:agents')) {
        showNotification('You do not have permission to delete servers', 'error');
        return;
    }
    if (
        !confirm(
            'Remove this server from Managed Systems? Unused records can be deleted to reduce clutter. This does not uninstall the agent from the host.'
        )
    ) {
        return;
    }
    try {
        await api.deleteAgent(agentId);
        showNotification('Server record deleted', 'success');
        await loadAgents();
    } catch (error) {
        console.error('[Agents] Delete failed:', error);
        showNotification(error instanceof Error ? error.message : 'Failed to delete server', 'error');
    }
}

// Make functions globally available
window.viewAgent = viewAgent;
window.triggerScan = triggerScan;
window.deleteManagedAgent = deleteManagedAgent;
window.loadAgents = loadAgents;
